import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { Amplify } from 'aws-amplify'
import { 
  signIn, 
  signOut, 
  confirmSignUp, 
  getCurrentUser,
  fetchAuthSession,
  resendSignUpCode,
  resetPassword,
  confirmResetPassword,
  confirmSignIn
} from '@aws-amplify/auth'
import { amplifyConfig } from '../config/cognito'

// Configure Amplify
Amplify.configure(amplifyConfig)

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const checkAuthState = useCallback(async () => {
    try {
      setLoading(true)
      const currentUser = await getCurrentUser()
      const session = await fetchAuthSession()
      
      if (currentUser && session.tokens) {
        const userGroups = session.tokens.accessToken.payload['cognito:groups'] || []
        setUser({
          ...currentUser,
          groups: userGroups,
          isAdmin: userGroups.includes('admins'),
          email: currentUser.signInDetails?.loginId || currentUser.username
        })
      }
    } catch (error) {
      console.log('No authenticated user:', error)
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [])

  // Check if user is authenticated on app load
  useEffect(() => {
    let ignore = false
    ;(async () => {
      try {
        await checkAuthState()
      } catch (error) {
        if (!ignore) console.log('No authenticated user:', error)
      }
    })()
    return () => { ignore = true }
  }, [checkAuthState])

  const login = async (email, password) => {
    try {
      setError(null)
      setLoading(true)
      
      const result = await signIn({
        username: email,
        password: password,
        options: {
          authFlowType: 'USER_PASSWORD_AUTH'
        }
      })
      
      console.log('Sign in result:', result) // Debug log
      
      if (result.isSignedIn) {
        await checkAuthState()
        return { success: true }
      } else {
        // Handle different challenge types
        if (result.nextStep) {
          console.log('Next step required:', result.nextStep)
          return { 
            success: false, 
            nextStep: result.nextStep,
            message: `Additional step required: ${result.nextStep.signInStep}`
          }
        }
        return { 
          success: false, 
          message: 'Sign in incomplete'
        }
      }
    } catch (error) {
      console.error('Login error:', error)
      let errorMessage = error.message || 'Login failed'
      
      // Provide more helpful error messages
      if (error.message?.includes('Incorrect username or password')) {
        errorMessage = 'Incorrect username or password. Note that username and password are case-sensitive. New members should use their username (not email) and temporary password from the invitation email.'
      } else if (error.message?.includes('User does not exist')) {
        errorMessage = 'User not found. Please check your username or contact an administrator.'
      }
      
      setError(errorMessage)
      return { 
        success: false, 
        error: errorMessage 
      }
    } finally {
      setLoading(false)
    }
  }

  const inviteUser = async (email, firstName, lastName, temporaryPassword) => {
    try {
      setError(null)
      setLoading(true)
      
      // This would typically call an admin API endpoint to create the user
      // For now, we'll simulate the process
      const response = await fetch('/api/admin/invite-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await fetchAuthSession()).tokens.accessToken}`
        },
        body: JSON.stringify({
          email,
          firstName,
          lastName,
          temporaryPassword
        })
      })
      
      if (response.ok) {
        return { success: true }
      } else {
        const errorData = await response.json()
        return { 
          success: false, 
          error: errorData.message || 'Failed to invite user' 
        }
      }
    } catch (error) {
      console.error('Invite user error:', error)
      setError(error.message || 'Failed to invite user')
      return { 
        success: false, 
        error: error.message || 'Failed to invite user' 
      }
    } finally {
      setLoading(false)
    }
  }

  const confirmRegistration = async (email, confirmationCode) => {
    try {
      setError(null)
      setLoading(true)
      
      await confirmSignUp({
        username: email,
        confirmationCode: confirmationCode
      })
      
      return { success: true }
    } catch (error) {
      console.error('Confirmation error:', error)
      setError(error.message || 'Confirmation failed')
      return { 
        success: false, 
        error: error.message || 'Confirmation failed' 
      }
    } finally {
      setLoading(false)
    }
  }

  const resendConfirmation = async (email) => {
    try {
      setError(null)
      await resendSignUpCode({ username: email })
      return { success: true }
    } catch (error) {
      console.error('Resend confirmation error:', error)
      setError(error.message || 'Failed to resend confirmation')
      return { 
        success: false, 
        error: error.message || 'Failed to resend confirmation' 
      }
    }
  }

  const logout = async () => {
    try {
      setError(null)
      await signOut()
      setUser(null)
      return { success: true }
    } catch (error) {
      console.error('Logout error:', error)
      setError(error.message || 'Logout failed')
      return { 
        success: false, 
        error: error.message || 'Logout failed' 
      }
    }
  }

  const forgotPassword = async (email) => {
    try {
      setError(null)
      await resetPassword({ username: email })
      return { success: true }
    } catch (error) {
      console.error('Forgot password error:', error)
      setError(error.message || 'Failed to send reset email')
      return { 
        success: false, 
        error: error.message || 'Failed to send reset email' 
      }
    }
  }

  const resetPasswordConfirm = async (email, confirmationCode, newPassword) => {
    try {
      setError(null)
      await confirmResetPassword({
        username: email,
        confirmationCode: confirmationCode,
        newPassword: newPassword
      })
      return { success: true }
    } catch (error) {
      console.error('Reset password confirmation error:', error)
      setError(error.message || 'Failed to reset password')
      return { 
        success: false, 
        error: error.message || 'Failed to reset password' 
      }
    }
  }

  const clearError = () => {
    setError(null)
  }

  const completeNewPassword = async (newPassword) => {
    try {
      setError(null)
      setLoading(true)
      
      const result = await confirmSignIn({
        challengeResponse: newPassword
      })
      
      if (result.isSignedIn) {
        await checkAuthState()
        return { success: true }
      } else {
        return { 
          success: false, 
          nextStep: result.nextStep,
          message: 'Additional steps required'
        }
      }
    } catch (error) {
      console.error('Complete new password error:', error)
      setError(error.message || 'Failed to set new password')
      return { 
        success: false, 
        error: error.message || 'Failed to set new password' 
      }
    } finally {
      setLoading(false)
    }
  }

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    isAdmin: user?.isAdmin || false,
    login,
    inviteUser,
    confirmRegistration,
    resendConfirmation,
    logout,
    forgotPassword,
    resetPasswordConfirm,
    completeNewPassword,
    clearError,
    checkAuthState
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}